import { useCallback, useState, useMemo } from 'react';
import { ProductCustomizationFragment } from '../../generated/types/ProductCustomizationFragment';

const getCustomizationMessageError = (
  customizationMessage: string,
  customizationParameters: ProductCustomizationFragment | null
) => {
  if (!customizationParameters) {
    return '';
  }

  const { characterClass, required, maxLength, minLength } = customizationParameters;

  if (!required && customizationMessage.length === 0) {
    return '';
  }

  if (required && !customizationMessage.length) {
    return `Please enter ${minLength}-${maxLength} characters.`;
  }

  if (minLength && customizationMessage.length < minLength) {
    return `Please enter ${minLength} or more characters.`;
  }

  if (maxLength && customizationMessage.length > maxLength) {
    return `Please enter ${maxLength} or less characters.`;
  }

  if (characterClass && !new RegExp(`^[${characterClass}]+$`).test(customizationMessage)) {
    return 'Please remove invalid characters.';
  }

  return '';
};

export const useProductCustomization = (
  customizationParameters: ProductCustomizationFragment | null
) => {
  const [customizationMessage, setCustomizationMessage] = useState('');
  const [customizationMessageError, setCustomizationMessageError] = useState('');

  const handleSetCustomizationMessage = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const message = evt.target.value;
      const error = getCustomizationMessageError(message, customizationParameters);

      setCustomizationMessage(message);
      setCustomizationMessageError(error);
    },
    [customizationParameters]
  );

  const isCustomizationValid = useMemo(
    () => !getCustomizationMessageError(customizationMessage, customizationParameters),
    [customizationMessage, customizationParameters]
  );

  return {
    customizationMessage,
    customizationMessageError,
    handleSetCustomizationMessage,
    isCustomizationValid
  };
};
